import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Text from 'core-system/Text'
import FileUpload from 'core-system/FileUpload/FileUpload'
import FlexContainer from 'core-system/FlexContainer'
import AlertBanner from 'core-system/AlertBanner'
import { uploadReceiptForReimbursement } from './uploadFile'
import FleetCardModal from './FleetCardModal'
import { EmployeeLocations } from 'shared/Router/EmployeeLocations'
import styled from 'styled-components'
import GridCard from 'employee-platform/shared/components/GridCard'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import palette from 'core-system/Themes/palette'
import { Transit, Parking } from 'core-system/Icons/Flex'
import { Rideshare, Car } from 'core-system/Icons/modeTypes'
import { ReactComponent as SuccessIcon } from 'features/Programs/Success/Components/success-icon.svg'

const Container = styled.div`
  padding: 2rem;
`

const Subtitle = styled(Text)`
  color: ${palette.text.secondary};
  margin-bottom: 1.5rem;
`

const ReceiptTypesSection = styled(FlexContainer)`
  margin-top: 1.5rem;
  align-items: center;
  gap: 1rem;
`

const ReceiptTypesContainer = styled(FlexContainer)`
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const ReceiptTypeLabel = styled.div`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
  line-height: 1.4;
  flex-shrink: 0;
  width: 4.5rem;
`

const Chip = styled(FlexContainer)`
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: ${palette.grey.grey100};
  border-radius: 0.5rem;
`

const ChipText = styled(Text)`
  color: ${palette.text.primary};
  font-weight: 500;
`

const SectionTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 0.75rem;
`

const GuidelineText = styled(Text)`
  color: ${palette.text.secondary};
  margin-bottom: 1.5rem;
  line-height: 1.5;
`

const ChecklistItem = styled(FlexContainer)`
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const PurpleSuccessIcon = styled(SuccessIcon)`
  width: 1.25rem;
  height: 1.25rem;
  color: ${palette.secondary.purple600};
`

const receiptTypes = [
  {
    label: 'Transit',
    icon: <Transit width={16} height={16} color={palette.text.secondary} />,
  },
  {
    label: 'Parking',
    icon: <Parking width={16} height={16} color={palette.text.secondary} />,
  },
  {
    label: 'Tolls',
    icon: <Car width={16} height={16} color={palette.text.secondary} />,
  },
  {
    label: 'Rideshare',
    icon: <Rideshare width={16} height={16} color={palette.text.secondary} />,
  },
]

const requiredInfo = [
  'Date of purchase',
  'Total amount paid',
  'Vendor/merchant name',
  'Description of service',
]

const ReimbursementView = React.memo(() => {
  const navigate = useNavigate()
  const [file, setFile] = useState([])
  const [showAlertBanner, setShowAlertBanner] = useState(false)
  const [showErrorBanner, setShowErrorBanner] = useState(false)
  const [showFleetCardModal, setShowFleetCardModal] = useState(true)

  const uploadReceiptToServer = async (files: File[]) => {
    const response = await uploadReceiptForReimbursement(files[0])
    if (response.status === 'success') {
      setFile([
        {
          name: 'Receipt',
          downloadUrl: response.downloadUrl,
          type: files[0].type,
        },
      ])
      setShowErrorBanner(false)
      setShowAlertBanner(true)
    } else {
      setShowAlertBanner(false)
      setShowErrorBanner(true)
    }
  }

  return (
    <Container>
      {showAlertBanner && (
        <AlertBanner
          variant='success'
          message='Upload successful!'
          closeFn={() => setShowAlertBanner(false)}
          marginBottom='1rem'
        />
      )}
      {showErrorBanner && (
        <AlertBanner
          variant='redAlert'
          message='There was an issue uploading your receipt. Make sure this is an accepted receipt type and that you have not previously uploaded this receipt for reimbursement.'
          closeFn={() => setShowErrorBanner(false)}
          marginBottom='1rem'
        />
      )}
      <GridContainer numColumns={2} fitContentHeight>
        <GridCard headerTitle='Submit Reimbursement'>
          <Subtitle variant='body1'>
            Upload receipts for your transit, parking, tolls, and rideshare
            expenses to be reimbursed.
          </Subtitle>
          <FileUpload
            placeholder='Click to upload or drag and drop'
            acceptedFilesTypes={['.png', '.jpeg', '.jpg', '.pdf']}
            placeholderSubtitle='PDF, JPG, or PNG (max. 10MB)'
            onChange={setFile}
            files={file}
            onError={() => null}
            uploadFiles={uploadReceiptToServer}
          />
          <ReceiptTypesSection>
            <ReceiptTypeLabel>Accepted receipt types:</ReceiptTypeLabel>
            <ReceiptTypesContainer>
              {receiptTypes.map((type) => (
                <Chip key={type.label}>
                  {type.icon}
                  <ChipText variant='body2'>{type.label}</ChipText>
                </Chip>
              ))}
            </ReceiptTypesContainer>
          </ReceiptTypesSection>
        </GridCard>

        <GridCard headerTitle='Reimbursement Guidelines'>
          <SectionTitle variant='body1'>
            What receipts can I upload?
          </SectionTitle>
          <GuidelineText variant='body2'>
            You can upload receipts for any qualifying transit, parking, toll,
            or rideshare expense. Make sure receipts clearly show the date,
            amount, and vendor name.
          </GuidelineText>
          <SectionTitle variant='body1'>Required Information</SectionTitle>
          {requiredInfo.map((item) => (
            <ChecklistItem key={item}>
              <PurpleSuccessIcon />
              <Text variant='body2' textColor={palette.text.secondary}>
                {item}
              </Text>
            </ChecklistItem>
          ))}
        </GridCard>
      </GridContainer>

      <FleetCardModal
        open={showFleetCardModal}
        onClose={() => setShowFleetCardModal(false)}
        onGetCard={() => navigate(EmployeeLocations.EmployeeSettings)}
        onContinue={() => setShowFleetCardModal(false)}
      />
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  ReimbursementView.displayName = 'ReimbursementView'
}

export default ReimbursementView
